import { dataDifine } from "../config/dataInterface";

/**
 * 当月の経過日数（1日 = 1）
 */
export function getDaysElapsed(): number {
  const now = new Date();
  return now.getDate();
}

/**
 * 当月の全日数
 */
export function getDaysInMonth(): number {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const next = new Date(year, month + 1, 0);
  return next.getDate();
}

/**
 * 前月の年・月（1月の場合は前年12月）
 */
export function getLastMonthYearMonth(): { year: number; month: number } {
  const now = new Date();
  now.setMonth(now.getMonth() - 1);
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * 分析APIの時系列データから「指定月の合計」を算出する（前月実績用）。
 * 月別データの場合は該当月の行を、日別の場合は該当月の全日の合計を返す。
 */
export function getMonthTotalFromAnalysys(
  data: dataDifine[],
  columns: string[],
  targetYear: number,
  targetMonth: number,
): number {
  if (!data?.length || !columns?.length) return 0;
  let total = 0;
  for (const row of data) {
    const name = String(row.name ?? "");
    const dateMatch = name.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (dateMatch) {
      const y = parseInt(dateMatch[1], 10);
      const m = parseInt(dateMatch[2], 10);
      if (y === targetYear && m === targetMonth) {
        total += getRowSum(row, columns);
      }
      continue;
    }
    const monthMatch = name.match(/(\d{4})年(\d{1,2})月/);
    if (monthMatch) {
      const y = parseInt(monthMatch[1], 10);
      const m = parseInt(monthMatch[2], 10);
      if (y === targetYear && m === targetMonth) {
        total += getRowSum(row, columns);
      }
    }
  }
  return total;
}

/**
 * 分析APIの時系列データから「当月1日〜今日」の合計を算出する。
 * data の各要素は name（日付または月）と各カラムの数値を持つ。
 * name が "YYYY-MM-DD" または "YYYY/MM/DD" 形式の場合は今日以前のみ加算。
 * 月別の場合は name が "YYYY年M月" 等のとき、当月ならその行の合計を返す。
 */
export function getMonthToDateTotalFromAnalysys(
  data: dataDifine[],
  columns: string[],
): number {
  if (!data?.length || !columns?.length) return 0;
  const today = new Date();
  const thisYear = today.getFullYear();
  const thisMonth = today.getMonth() + 1;
  const thisDay = today.getDate();

  let total = 0;
  for (const row of data) {
    const name = String(row.name ?? "");
    // 日別: YYYY-MM-DD または YYYY/MM/DD
    const dateMatch = name.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (dateMatch) {
      const y = parseInt(dateMatch[1], 10);
      const m = parseInt(dateMatch[2], 10);
      const d = parseInt(dateMatch[3], 10);
      if (y < thisYear || (y === thisYear && m < thisMonth)) {
        total += getRowSum(row, columns);
        continue;
      }
      if (y === thisYear && m === thisMonth && d <= thisDay) {
        total += getRowSum(row, columns);
      }
      continue;
    }
    // 月別: 当月のみ加算（1行のみ想定）
    const monthMatch = name.match(/(\d{4})年(\d{1,2})月/);
    if (monthMatch) {
      const y = parseInt(monthMatch[1], 10);
      const m = parseInt(monthMatch[2], 10);
      if (y === thisYear && m === thisMonth) {
        total += getRowSum(row, columns);
      }
      continue;
    }
    // フォーマット不明な場合は全加算（フォールバック）
    total += getRowSum(row, columns);
  }
  return total;
}

function getRowSum(row: dataDifine, columns: string[]): number {
  let sum = 0;
  for (const col of columns) {
    const val = row[col];
    if (typeof val === "number" && !Number.isNaN(val)) sum += val;
  }
  return sum;
}

/**
 * 日割合計（月末見込み）= 月間合計 ÷ 経過日数 × 当月全日数
 * 経過日数が 0 の場合は 0 を返す。
 */
export function getProratedTotal(
  monthToDateTotal: number,
  daysElapsed: number,
  daysInMonth: number,
): number {
  if (daysElapsed <= 0 || daysInMonth <= 0) return 0;
  return Math.round((monthToDateTotal / daysElapsed) * daysInMonth);
}
